import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { getFormScreenForVirtualCard } from 'src/app/UpdateVirtualCardForm/getFormScreenForVirtualCard'
import { createSetReceiptAttachmentIds } from 'src/app/UpdateVirtualCardForm/redux/updateVirtualCardForm.actions'
import {
  selectBalanceLockEnabledCreditCardsFullPaginatedData,
  selectCreditCardsFullPaginatedData,
  selectErrorDetailMapFromState,
  selectFilteredBalanceLockEnabledCreditCardOptions,
  selectFilteredCreditCardOptions,
  selectFilteredGuestAccessEnabledCreditCardOptions,
  selectFilteredRecurringEnabledCreditCardOptions,
  selectGuestAccessEnabledCreditCardsFullPaginatedData,
  selectPuttingVirtualCardCancelFromState,
  selectPuttingVirtualCardCancelUpdateFromState,
  selectPuttingVirtualCardFailureFromState,
  selectPuttingVirtualCardFromState,
  selectPuttingVirtualCardRejectFromState,
  selectReceiptAttachmentModelsList,
  selectRecurringEnabledCreditCardsFullPaginatedData,
  selectTopLevelErrorFromState,
  selectUploadingAttachmentsFromState,
  selectVirtualCardCardholder,
  selectVirtualCardPermissions,
  selectVirtualCardRecipient,
} from 'src/app/UpdateVirtualCardForm/redux/updateVirtualCardForm.selectors'
import { UpdateVirtualCardScreen } from 'src/app/UpdateVirtualCardForm/UpdateVirtualCardScreen'
import {
  CreditCardDropdownInfiniteScrollProps,
  creditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundRightFlyoutContent from 'src/components/NotFoundRightFlyoutContent/NotFoundRightFlyoutContent'
import UpdateVirtualCardBillPayContent from 'src/components/UpdateVirtualCardBillPayContent/UpdateVirtualCardBillPayContent'
import UpdateVirtualCardGuestAccessContent from 'src/components/UpdateVirtualCardGuestAccessContent/UpdateVirtualCardGuestAccessContent'
import UpdateVirtualCardIssuerCycleContent from 'src/components/UpdateVirtualCardIssuerCycleContent/UpdateVirtualCardIssuerCycleContent'
import UpdateVirtualCardPickContent from 'src/components/UpdateVirtualCardPickContent/UpdateVirtualCardPickContent'
import UpdateVirtualCardRecurringContent from 'src/components/UpdateVirtualCardRecurringContent/UpdateVirtualCardRecurringContent'
import UpdateVirtualCardStandardContent from 'src/components/UpdateVirtualCardStandardContent/UpdateVirtualCardStandardContent'
import { ExtendCurrency, VirtualCardTypes } from 'src/enums'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useFlyoutDispatch } from 'src/hooks'
import { useVirtualCardFormStatistics } from 'src/hooks/virtualCardForm'
import Trns from 'src/localization/Trns'
import { CreditCardStatus } from 'src/models/creditCard.models'
import { CreditCardPermissions } from 'src/models/creditCardPermissions.models'
import {
  ApprovalStatus,
  ToastType,
  VirtualCardSecureModel,
  VirtualCardType,
} from 'src/models/virtualCard.models'
import {
  useVirtualCardFormCreditCards,
  type VirtualCardFormScreen,
} from 'src/queries/creditCards/domain/useVirtualCardFormCreditCards'
import {
  automationRulesQueryKeys,
  transactionsQueryKeys,
} from 'src/queries/queries.constants'
import { createSetIsRequestCompletedPopupOpen } from 'src/redux/actions/app.actions'
import { getCountrys } from 'src/redux/actions/data/countrys.actions'
import {
  getCreditCard,
  getCreditCards,
} from 'src/redux/actions/data/creditCards.actions'
import { getOrganizationCardApprovalsEnabled } from 'src/redux/actions/data/organizations.actions'
import { uploadAttachments } from 'src/redux/actions/data/receiptAttachments.actions'
import {
  putVirtualCardAndOpenToastAndCloseFlyout,
  putVirtualCardCancelAndCloseModalOrRedirect,
  putVirtualCardCancelUpdateAndOpenToastAndCloseModal,
  putVirtualCardFinalApprovalAndOpenToastAndCloseFlyout,
  putVirtualCardRejectAndOpenToastAndCloseModal,
} from 'src/redux/actions/data/virtualCards.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectCardApprovalsEnabled,
  selectCardApprovalsFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import {
  selectCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import {
  selectAppName,
  selectBrandFeaturesFromState,
} from 'src/redux/selectors/session.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PutVirtualCardRequest } from 'src/requests/putVirtualCard.request'
import { toDollars } from 'src/utils/currency.utils'

interface UpdateVirtualCardFormProps {
  virtualCardId: string
  virtualCard: VirtualCardSecureModel | undefined
  loadingFlyout: boolean
  screen?: UpdateVirtualCardScreen
}

const UpdateVirtualCardForm: React.FC<UpdateVirtualCardFormProps> = (props) => {
  const { virtualCardId, virtualCard, loadingFlyout } = props
  const queryClient = useQueryClient()
  const queryParams = useSelector(selectQueryParams)
  const cardholder = useSelector(selectVirtualCardCardholder)
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const puttingVirtualCard = useSelector(selectPuttingVirtualCardFromState)
  const puttingVirtualCardCancel = useSelector(
    selectPuttingVirtualCardCancelFromState,
  )
  const puttingVirtualCardCancelUpdate = useSelector(
    selectPuttingVirtualCardCancelUpdateFromState,
  )
  const puttingVirtualCardReject = useSelector(
    selectPuttingVirtualCardRejectFromState,
  )
  const receiptAttachments = useSelector(selectReceiptAttachmentModelsList)
  const recipient = useSelector(selectVirtualCardRecipient)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const uploadingAttachmentThumbnails = useSelector(
    selectUploadingAttachmentsFromState,
  )
  const creditCardsFullPaginatedData = useSelector(
    selectCreditCardsFullPaginatedData,
  )
  const filteredCreditCardOptions = useSelector(selectFilteredCreditCardOptions)
  const recurringEnabledCreditCardsFullPaginatedData = useSelector(
    selectRecurringEnabledCreditCardsFullPaginatedData,
  )
  const filteredRecurringEnabledCreditCardOptions = useSelector(
    selectFilteredRecurringEnabledCreditCardOptions,
  )
  const guestAccessEnabledCreditCardsFullPaginatedData = useSelector(
    selectGuestAccessEnabledCreditCardsFullPaginatedData,
  )
  const filteredGuestAccessEnabledCreditCardOptions = useSelector(
    selectFilteredGuestAccessEnabledCreditCardOptions,
  )
  const balanceLockedEnabledCreditCardsFullPaginatedData = useSelector(
    selectBalanceLockEnabledCreditCardsFullPaginatedData,
  )
  const filteredBalanceLockEnabledCreditCardOptions = useSelector(
    selectFilteredBalanceLockEnabledCreditCardOptions,
  )
  const virtualCardPermissions = useSelector(selectVirtualCardPermissions)
  const appName = useSelector(selectAppName)
  const showRequestCompletedPopup = useSelector(
    selectPuttingVirtualCardFailureFromState,
  )
  const userOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const cardApprovalsEnabled = useSelector(selectCardApprovalsEnabled)
  const cardApprovalRules = useSelector(selectCardApprovalsFromState)
  const user = useSelector(selectUserListModelFromState)
  const { recurringVirtualCardsEnabled } = useSelector(
    selectBrandFeaturesFromState,
  )
  const enableUnifiedBillPayForms =
    useSelector(selectFeatureFlaggerFromState)?.enable_unified_bill_pay_forms ??
    false

  const enableNewFormHooks =
    useSelector(selectFeatureFlaggerFromState)?.enable_new_form_hooks ?? false

  const { analytics } = useAnalyticsContext()

  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const creditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCount =
    useSelector(
      selectCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFromState,
    )
  const creditCardsActiveCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  )
  const creditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCount =
    useSelector(
      selectCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFromState,
    )
  const creditCardsActiveSourceCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsFromState,
  )
  const creditCardsActiveDirectEnabledCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsFromState,
  )
  const creditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCount =
    useSelector(
      selectCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsFromState,
    )
  const creditCardsActiveStandardCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsFromState,
  )
  const creditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCount =
    useSelector(
      selectCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFromState,
    )
  const creditCardsActiveSourceStandardCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsFromState,
  )
  const creditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCount =
    useSelector(
      selectCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFromState,
    )

  // New React Query hooks — used when enableNewFormHooks is true
  // Also gate on virtualCard being loaded to avoid fetching with wrong screen type
  const newHooksEnabled = enableNewFormHooks && virtualCard !== undefined
  const formStats = useVirtualCardFormStatistics({
    variant: 'update',
    enabled: newHooksEnabled,
  })

  const updateScreenToFormScreen: Record<
    UpdateVirtualCardScreen,
    VirtualCardFormScreen
  > = {
    [UpdateVirtualCardScreen.Standard]: 'Standard',
    [UpdateVirtualCardScreen.BillPay]: 'BillPay',
    [UpdateVirtualCardScreen.Recurring]: 'Recurring',
    [UpdateVirtualCardScreen.GuestAccess]: 'GuestAccess',
    [UpdateVirtualCardScreen.RecurringGuest]: 'RecurringGuest',
    [UpdateVirtualCardScreen.DecliningBalance]: 'DecliningBalance',
    [UpdateVirtualCardScreen.IssuerCycle]: 'IssuerCycle',
    [UpdateVirtualCardScreen.Pick]: 'Standard',
  }

  const activeUpdateFormScreen: VirtualCardFormScreen =
    props.screen !== undefined
      ? updateScreenToFormScreen[props.screen]
      : getFormScreenForVirtualCard(virtualCard)

  const formCreditCards = useVirtualCardFormCreditCards(
    activeUpdateFormScreen,
    {
      billPayEnabled: activeUpdateFormScreen === 'BillPay' ? true : undefined,
      enabled: newHooksEnabled,
    },
  )

  const formCreditCardStandardOptions = useMemo(
    () => formCreditCards.standard.options.map((cc) => creditCardOption(cc)),
    [formCreditCards.standard.options],
  )
  const formCreditCardRecurringOptions = useMemo(
    () => formCreditCards.recurring.options.map((cc) => creditCardOption(cc)),
    [formCreditCards.recurring.options],
  )
  const formCreditCardGuestAccessOptions = useMemo(
    () => formCreditCards.guestAccess.options.map((cc) => creditCardOption(cc)),
    [formCreditCards.guestAccess.options],
  )
  const formCreditCardBalanceLockOptions = useMemo(
    () => formCreditCards.balanceLock.options.map((cc) => creditCardOption(cc)),
    [formCreditCards.balanceLock.options],
  )
  const formCreditCardIssuerCycleOptions = useMemo(
    () => formCreditCards.issuerCycle.options.map((cc) => creditCardOption(cc)),
    [formCreditCards.issuerCycle.options],
  )

  const canUpdateVirtualCard =
    virtualCardPermissions?.canUpdateVirtualCard || false
  const showPreApprovalSoftAlert =
    virtualCard?.isPending && virtualCard?.recipientId !== user?.id
      ? cardApprovalsEnabled && cardApprovalRules?.approverId !== user?.id
      : cardApprovalsEnabled &&
        cardApprovalRules?.approverId !== user?.id &&
        virtualCardPermissions?.canUpdateVirtualCard

  const dispatch = useFlyoutDispatch()

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  useEffect(() => {
    if (cardApprovalRules === undefined && userOrganization !== undefined) {
      dispatch(getOrganizationCardApprovalsEnabled(userOrganization.id))
    }
  }, [cardApprovalRules])
  useEffect((): void => {
    if (enableNewFormHooks) return
    dispatchStatistics([
      appStatisticsKeys.CreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveSourceCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveStandardCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatistics,
    ])
  }, [dispatchStatistics, enableNewFormHooks])

  useEffect((): void => {
    if (typeof queryParams.creditCardId === 'string') {
      dispatch(getCreditCard(queryParams.creditCardId))
    }
  }, [queryParams['creditCardId']])

  useEffect((): void => {
    if (
      (virtualCardPermissions?.canUpdateVirtualCard &&
        virtualCard?.creditCardId !== undefined) ||
      (virtualCard?.creditCardId !== undefined &&
        virtualCard.awaitingApprovalStatus !== undefined)
    ) {
      dispatch(getCreditCard(virtualCard?.creditCardId))
    }
  }, [virtualCardPermissions, virtualCard?.creditCardId])

  useEffect((): void => {
    dispatch(getCountrys())
  }, [])

  useEffect((): void => {
    if (showRequestCompletedPopup) {
      dispatch(createSetIsRequestCompletedPopupOpen(true))
    }
  }, [showRequestCompletedPopup])

  const dispatchUploadAttachments = (
    attachments: PostReceiptAttachmentRequest[],
  ): void => {
    dispatch(uploadAttachments(attachments))
  }
  const dispatchPutVirtualCardAndOpenToastAndCloseFlyout = (
    id: string,
    body: Partial<PutVirtualCardRequest>,
    openToast?: boolean,
  ): void => {
    // if the current user isnt the recipient or the card approver, and card approvals are enabled
    const finalApprovalToastBool =
      virtualCard?.recipient?.id !== user?.id &&
      cardApprovalsEnabled &&
      cardApprovalRules?.approverId !== user?.id
    // if the balance is greater than the threshold, and card approvals are enabled
    const showCardApproverToast =
      body.currency === ExtendCurrency.Cad
        ? body.balanceCents !== undefined &&
          cardApprovalRules?.thresholds?.cadCents !== undefined &&
          body.balanceCents > cardApprovalRules?.thresholds.cadCents
        : body.balanceCents !== undefined &&
          cardApprovalRules?.thresholds?.usdCents !== undefined &&
          body.balanceCents > cardApprovalRules?.thresholds.usdCents
    const toastType =
      finalApprovalToastBool && showCardApproverToast && virtualCard?.isPending
        ? ToastType.ApproveRequestOrCreate
        : finalApprovalToastBool && virtualCard?.isActive
          ? ToastType.ApproveUpdate
          : virtualCard?.cardType === VirtualCardType.BillPay
            ? ToastType.BillPayApprove
            : undefined
    // if card approvals are enabled and the current user is the card approver but not the funding source owner
    const virtualCardFinalApproval =
      cardApprovalsEnabled &&
      cardApprovalRules?.approverId === user?.id &&
      virtualCard?.awaitingApprovalStatus ===
        ApprovalStatus.AwaitingApproverRuleApproval

    const virtualCardResult = virtualCardFinalApproval
      ? dispatch(
          putVirtualCardFinalApprovalAndOpenToastAndCloseFlyout(
            id,
            { pendingRevisionId: body.pendingRevisionId ?? null },
            openToast,
            toastType,
            virtualCard?.billPayMeta?.vendorName,
          ),
        )
      : dispatch(
          putVirtualCardAndOpenToastAndCloseFlyout(
            id,
            body,
            openToast,
            toastType,
          ),
        )

    // TODO: React Query move into onSuccess of virtualCard queries when we refactor virtual cards
    virtualCardResult.then(() => {
      queryClient.invalidateQueries({ queryKey: transactionsQueryKeys.all })
      queryClient.invalidateQueries({
        queryKey: automationRulesQueryKeys.all(),
      })
    })
  }
  const dispatchPutVirtualCardCancelAndCloseModalOrRedirect = (
    id: string,
    isPending: boolean,
  ): void => {
    dispatch(putVirtualCardCancelAndCloseModalOrRedirect(id, isPending))
  }
  const dispatchPutVirtualCardRejectAndCloseModal = (id: string): void => {
    dispatch(
      putVirtualCardRejectAndOpenToastAndCloseModal(
        id,
        virtualCard?.pending?.id,
        virtualCard?.cardType,
      ),
    )
  }
  const dispatchPutVirtualCardCancelUpdateAndCloseModal = (
    id: string,
  ): void => {
    dispatch(putVirtualCardCancelUpdateAndOpenToastAndCloseModal(id))
  }

  const deleteAttachment = (attachmentId: string): void => {
    dispatch(
      createSetReceiptAttachmentIds(
        receiptAttachments
          .filter((att) => att.id !== attachmentId)
          .map((att) => att.id),
      ),
    )
  }
  const isOrganizationCardApproverMe =
    user?.id === cardApprovalRules?.approverId

  const currentScreen =
    props.screen ??
    (virtualCard !== undefined
      ? virtualCard.isIssuerCycle
        ? UpdateVirtualCardScreen.IssuerCycle
        : virtualCard.features.balanceLocked
          ? UpdateVirtualCardScreen.DecliningBalance
          : virtualCard.direct
            ? virtualCard.recurs
              ? UpdateVirtualCardScreen.RecurringGuest
              : UpdateVirtualCardScreen.GuestAccess
            : (virtualCard.recurs &&
                  virtualCard.pending?.recurs === undefined) ||
                (!virtualCard.recurs && virtualCard.pending?.recurs)
              ? UpdateVirtualCardScreen.Recurring
              : UpdateVirtualCardScreen.Standard
      : UpdateVirtualCardScreen.Pick)
  const [screen, setScreen] = useState(currentScreen)

  useEffect((): void => {
    setScreen(currentScreen)
  }, [currentScreen])

  useEffect((): void => {
    if (virtualCard?.pending?.receiptAttachments !== undefined) {
      dispatch(
        createSetReceiptAttachmentIds(
          virtualCard?.pending?.receiptAttachments?.map((att) => att.id),
        ),
      )
    } else if (virtualCard?.receiptAttachments !== undefined) {
      dispatch(
        createSetReceiptAttachmentIds(
          virtualCard.receiptAttachments.map((att) => att.id),
        ),
      )
    }
  }, [screen, virtualCard])

  const negativeButton = virtualCard?.isPending
    ? {
        loading: canUpdateVirtualCard
          ? puttingVirtualCardReject
          : puttingVirtualCardCancel,
        text:
          canUpdateVirtualCard ||
          (isOrganizationCardApproverMe &&
            virtualCard.cardholderId !== user?.id) ? (
            <Trns source={'Reject Card Request'} />
          ) : (
            <Trns source={'Cancel Card Request'} />
          ),
        onClick: (): void => {
          if (canUpdateVirtualCard || isOrganizationCardApproverMe) {
            dispatchPutVirtualCardRejectAndCloseModal(virtualCard.id)
            analytics?.track('manage vcn request', {
              type_of_card: virtualCard.cardTypeLabel.props.source,
              card_name: virtualCard.displayName,
              credit_limit: toDollars(virtualCard.balanceCents),
              active_until:
                virtualCard.activeUntilTimezonedMoment.format('YYYY-MM-DD'),
              add_attachments:
                virtualCard.receiptAttachments !== undefined &&
                virtualCard.receiptAttachments.length > 0,
              add_notes: virtualCard.notes !== undefined,
              card_Created: false,
              request_status: 'Reject',
            })
          } else {
            dispatchPutVirtualCardCancelAndCloseModalOrRedirect(
              virtualCard.id,
              virtualCard.isPending,
            )
          }
        },
      }
    : virtualCard?.pending !== undefined
      ? {
          loading: canUpdateVirtualCard
            ? puttingVirtualCardReject
            : puttingVirtualCardCancelUpdate,
          text:
            canUpdateVirtualCard ||
            (isOrganizationCardApproverMe &&
              virtualCard.cardholderId !== user?.id) ? (
              <Trns source={'Reject Update Request'} />
            ) : (
              <Trns source={'Cancel Update Request'} />
            ),
          onClick: (): void => {
            if (
              canUpdateVirtualCard ||
              (isOrganizationCardApproverMe &&
                virtualCard.cardholderId !== user?.id)
            ) {
              dispatchPutVirtualCardRejectAndCloseModal(virtualCard?.id)
            } else {
              dispatchPutVirtualCardCancelUpdateAndCloseModal(virtualCard.id)
            }
          },
        }
      : undefined

  // const hasCardsWithRequestedCurrency = issuingEnabledActiveRcnOptions.some((rcn) => rcn.details?.creditCard.currency === virtualCard?.currency)
  // const hasBudgetsWithRequestedCurrency = issuingEnabledActiveBudgetOptions.some((b) => b.details?.creditCard.currency === virtualCard?.currency)
  // const currencyError = virtualCard?.isPending && !hasCardsWithRequestedCurrency && !hasBudgetsWithRequestedCurrency && canUpdateVirtualCard
  const currencyError = false // TODO: seems low priority to me, but if we want this needs stat

  const fetchCreditCards = (filterInput?: string, page?: number): void => {
    dispatch(
      getCreditCards({
        search: filterInput,
        count: 10,
        page: page,
        status: CreditCardStatus.Active,
        withPermission: CreditCardPermissions.IssueVirtualCards,
        billPayEnabled:
          currentScreen === UpdateVirtualCardScreen.BillPay ? true : undefined,
        balanceLockedEnabled: false,
        sortField: 'type',
        sortDirection: 'ASC',
      }),
    )
  }
  const creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps = {
    fetch: fetchCreditCards,
    ...creditCardsFullPaginatedData,
  }

  const fetchRecurringEnabledCreditCards = (
    filterInput?: string,
    page?: number,
  ): void => {
    dispatch(
      getCreditCards({
        search: filterInput,
        count: 10,
        page: page,
        status: CreditCardStatus.Active,
        withPermission: CreditCardPermissions.IssueVirtualCards,
        sortField: 'type',
        sortDirection: 'ASC',
        recurrenceEnabled: true,
      }),
    )
  }
  const recurringEnabledCreditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps =
    {
      fetch: fetchRecurringEnabledCreditCards,
      ...recurringEnabledCreditCardsFullPaginatedData,
    }

  const fetchGuestAccessEnabledCreditCards = (
    filterInput?: string,
    page?: number,
  ): void => {
    dispatch(
      getCreditCards({
        search: filterInput,
        count: 10,
        page: page,
        status: CreditCardStatus.Active,
        withPermission: CreditCardPermissions.IssueVirtualCards,
        sortField: 'type',
        sortDirection: 'ASC',
        directEnabled: true,
      }),
    )
  }
  const guestAccessEnabledCreditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps =
    {
      fetch: fetchGuestAccessEnabledCreditCards,
      ...guestAccessEnabledCreditCardsFullPaginatedData,
    }

  const fetchBalanceLockEnabledCreditCards = (
    filterInput?: string,
    page?: number,
  ): void => {
    dispatch(
      getCreditCards({
        search: filterInput,
        count: 10,
        page: page,
        status: CreditCardStatus.Active,
        withPermission: CreditCardPermissions.IssueVirtualCards,
        sortField: 'type',
        sortDirection: 'ASC',
        balanceLockedEnabled: true,
      }),
    )
  }
  const balanceLockedEnabledCreditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps =
    {
      fetch: fetchBalanceLockEnabledCreditCards,
      ...balanceLockedEnabledCreditCardsFullPaginatedData,
    }

  return (
    <>
      {loadingFlyout ||
      !(enableNewFormHooks
        ? formStats.allLoaded
        : statisticsLoaded.creditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsLoaded &&
          statisticsLoaded.creditCardsActiveCanIssueVirtualCardsCountStatisticsLoaded &&
          statisticsLoaded.creditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsLoaded &&
          statisticsLoaded.creditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsLoaded &&
          statisticsLoaded.creditCardsActiveSourceCanIssueVirtualCardsCountStatisticsLoaded &&
          statisticsLoaded.creditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsLoaded &&
          statisticsLoaded.creditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsLoaded &&
          statisticsLoaded.creditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsLoaded &&
          statisticsLoaded.creditCardsActiveStandardCanIssueVirtualCardsCountStatisticsLoaded &&
          statisticsLoaded.creditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsLoaded) ? (
        <LoadingBackgroundPage />
      ) : virtualCard !== undefined &&
        !virtualCard.isCancelledOrRejectedRequest &&
        virtualCardPermissions !== undefined &&
        (!virtualCardPermissions.canReadOnly || isOrganizationCardApproverMe) &&
        recipient !== undefined &&
        cardholder !== undefined &&
        !virtualCard.isClosed ? (
        <>
          <Helmet>
            <title>{`Update Virtual Card | ${appName}`}</title>
            <meta
              name="description"
              content={`Update Virtual Card page of ${appName}`}
            />
          </Helmet>
          {screen === UpdateVirtualCardScreen.Pick && (
            <UpdateVirtualCardPickContent
              id={virtualCardId}
              setScreen={setScreen}
              virtualCard={virtualCard}
              virtualCardPermissions={virtualCardPermissions}
              hideRecurring={!recurringVirtualCardsEnabled}
            />
          )}
          {screen === UpdateVirtualCardScreen.Standard && (
            <UpdateVirtualCardStandardContent
              cardholder={cardholder}
              currencyError={currencyError}
              deleteAttachment={deleteAttachment}
              errorDetailMap={errorDetailMap}
              negativeButton={negativeButton}
              puttingVirtualCard={puttingVirtualCard}
              putVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPutVirtualCardAndOpenToastAndCloseFlyout
              }
              receiptAttachments={receiptAttachments}
              recipient={recipient}
              setScreen={setScreen}
              topLevelError={topLevelError}
              uploadAttachments={dispatchUploadAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              userOptions={[]}
              virtualCard={virtualCard}
              isFormDisabled={
                virtualCard.isPending &&
                canUpdateVirtualCard &&
                (enableNewFormHooks
                  ? formStats.standardCount === 0
                  : creditCardsActiveStandardCanIssueVirtualCardsCount === 0)
              }
              labelCreditCardsAsAccounts={
                enableNewFormHooks
                  ? formStats.standardSourceCount > 0
                  : creditCardsActiveSourceStandardCanIssueVirtualCardsCount > 0
              }
              creditCardOptions={
                enableNewFormHooks
                  ? formCreditCardStandardOptions
                  : filteredCreditCardOptions
              }
              creditCardInfiniteScrollProps={
                enableNewFormHooks
                  ? formCreditCards.standard.infiniteScrollProps
                  : creditCardInfiniteScrollProps
              }
              virtualCardPermissions={virtualCardPermissions}
              showSoftAlert={showPreApprovalSoftAlert}
              cardApproverId={cardApprovalRules?.approverId}
            />
          )}
          {screen === UpdateVirtualCardScreen.Recurring && (
            <UpdateVirtualCardRecurringContent
              virtualCard={virtualCard}
              recipient={recipient}
              cardholder={cardholder}
              userOptions={[]}
              receiptAttachments={receiptAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              puttingVirtualCard={puttingVirtualCard}
              putVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPutVirtualCardAndOpenToastAndCloseFlyout
              }
              uploadAttachments={dispatchUploadAttachments}
              deleteAttachment={deleteAttachment}
              setScreen={setScreen}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              negativeButton={negativeButton}
              currencyError={currencyError}
              isFormDisabled={
                virtualCard.isPending &&
                canUpdateVirtualCard &&
                (enableNewFormHooks
                  ? formStats.recurringCount === 0
                  : creditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCount ===
                    0)
              }
              labelCreditCardsAsAccounts={
                enableNewFormHooks
                  ? formStats.recurringSourceCount > 0
                  : creditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCount >
                    0
              }
              creditCardOptions={
                enableNewFormHooks
                  ? formCreditCardRecurringOptions
                  : filteredRecurringEnabledCreditCardOptions
              }
              creditCardInfiniteScrollProps={
                enableNewFormHooks
                  ? formCreditCards.recurring.infiniteScrollProps
                  : recurringEnabledCreditCardInfiniteScrollProps
              }
              virtualCardPermissions={virtualCardPermissions}
              showSoftAlert={showPreApprovalSoftAlert}
              cardApproverId={cardApprovalRules?.approverId}
            />
          )}
          {screen === UpdateVirtualCardScreen.RecurringGuest && (
            <UpdateVirtualCardRecurringContent
              virtualCard={virtualCard}
              recipient={recipient}
              cardholder={cardholder}
              userOptions={[]}
              receiptAttachments={receiptAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              puttingVirtualCard={puttingVirtualCard}
              putVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPutVirtualCardAndOpenToastAndCloseFlyout
              }
              uploadAttachments={dispatchUploadAttachments}
              deleteAttachment={deleteAttachment}
              setScreen={setScreen}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              negativeButton={negativeButton}
              currencyError={currencyError}
              isFormDisabled={
                virtualCard.isPending &&
                canUpdateVirtualCard &&
                (enableNewFormHooks
                  ? formStats.recurringCount === 0
                  : creditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCount ===
                    0)
              }
              labelCreditCardsAsAccounts={
                enableNewFormHooks
                  ? formStats.recurringSourceCount > 0
                  : creditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCount >
                    0
              }
              creditCardOptions={
                enableNewFormHooks
                  ? formCreditCardRecurringOptions
                  : filteredRecurringEnabledCreditCardOptions
              }
              creditCardInfiniteScrollProps={
                enableNewFormHooks
                  ? formCreditCards.recurring.infiniteScrollProps
                  : recurringEnabledCreditCardInfiniteScrollProps
              }
              virtualCardPermissions={virtualCardPermissions}
              isGuestCard={virtualCard?.direct}
              showSoftAlert={
                virtualCardPermissions.canUpdateVirtualCard &&
                cardApprovalsEnabled &&
                cardApprovalRules?.approverId !== user?.id
              }
              cardApproverId={cardApprovalRules?.approverId}
            />
          )}
          {screen === UpdateVirtualCardScreen.GuestAccess && (
            <UpdateVirtualCardGuestAccessContent
              virtualCard={virtualCard}
              recipient={recipient}
              cardholder={cardholder}
              userOptions={[]}
              receiptAttachments={receiptAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              puttingVirtualCard={puttingVirtualCard}
              putVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPutVirtualCardAndOpenToastAndCloseFlyout
              }
              uploadAttachments={dispatchUploadAttachments}
              deleteAttachment={deleteAttachment}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              negativeButton={negativeButton}
              isFormDisabled={
                virtualCard.isPending &&
                canUpdateVirtualCard &&
                (enableNewFormHooks
                  ? formStats.guestAccessCount === 0
                  : creditCardsActiveDirectEnabledCanIssueVirtualCardsCount ===
                    0)
              }
              labelCreditCardsAsAccounts={
                enableNewFormHooks
                  ? formStats.guestAccessSourceCount > 0
                  : creditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCount >
                    0
              }
              creditCardOptions={
                enableNewFormHooks
                  ? formCreditCardGuestAccessOptions
                  : filteredGuestAccessEnabledCreditCardOptions
              }
              creditCardInfiniteScrollProps={
                enableNewFormHooks
                  ? formCreditCards.guestAccess.infiniteScrollProps
                  : guestAccessEnabledCreditCardInfiniteScrollProps
              }
              virtualCardPermissions={virtualCardPermissions}
              showSoftAlert={
                virtualCardPermissions.canUpdateVirtualCard &&
                cardApprovalsEnabled &&
                cardApprovalRules?.approverId !== user?.id
              }
              cardApproverId={cardApprovalRules?.approverId}
            />
          )}
          {screen === UpdateVirtualCardScreen.BillPay &&
            (enableUnifiedBillPayForms ? (
              <UpdateVirtualCardStandardContent
                cardholder={cardholder}
                currencyError={currencyError}
                deleteAttachment={deleteAttachment}
                errorDetailMap={errorDetailMap}
                negativeButton={negativeButton}
                puttingVirtualCard={puttingVirtualCard}
                putVirtualCardAndOpenToastAndCloseFlyout={
                  dispatchPutVirtualCardAndOpenToastAndCloseFlyout
                }
                receiptAttachments={receiptAttachments}
                recipient={recipient}
                setScreen={setScreen}
                topLevelError={topLevelError}
                uploadAttachments={dispatchUploadAttachments}
                uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
                userOptions={[]}
                virtualCard={virtualCard}
                isFormDisabled={
                  virtualCard.isPending &&
                  canUpdateVirtualCard &&
                  (enableNewFormHooks
                    ? formStats.billPayCount === 0
                    : creditCardsActiveCanIssueVirtualCardsCount === 0)
                }
                labelCreditCardsAsAccounts={
                  enableNewFormHooks
                    ? formStats.billPaySourceCount > 0
                    : creditCardsActiveSourceCanIssueVirtualCardsCount > 0
                }
                creditCardOptions={
                  enableNewFormHooks
                    ? formCreditCardStandardOptions
                    : filteredCreditCardOptions
                }
                creditCardInfiniteScrollProps={
                  enableNewFormHooks
                    ? formCreditCards.standard.infiniteScrollProps
                    : creditCardInfiniteScrollProps
                }
                virtualCardPermissions={virtualCardPermissions}
                showSoftAlert={showPreApprovalSoftAlert}
                cardApproverId={cardApprovalRules?.approverId}
                isBillPay
              />
            ) : (
              <UpdateVirtualCardBillPayContent
                cardholder={cardholder}
                currencyError={currencyError}
                deleteAttachment={deleteAttachment}
                errorDetailMap={errorDetailMap}
                negativeButton={negativeButton}
                puttingVirtualCard={puttingVirtualCard}
                putVirtualCardAndOpenToastAndCloseFlyout={
                  dispatchPutVirtualCardAndOpenToastAndCloseFlyout
                }
                receiptAttachments={receiptAttachments}
                recipient={recipient}
                topLevelError={topLevelError}
                uploadAttachments={dispatchUploadAttachments}
                uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
                virtualCard={virtualCard}
                isFormDisabled={
                  virtualCard.isPending &&
                  canUpdateVirtualCard &&
                  (enableNewFormHooks
                    ? formStats.billPayCount === 0
                    : creditCardsActiveCanIssueVirtualCardsCount === 0)
                }
                labelCreditCardsAsAccounts={
                  enableNewFormHooks
                    ? formStats.billPaySourceCount > 0
                    : creditCardsActiveSourceCanIssueVirtualCardsCount > 0
                }
                creditCardOptions={
                  enableNewFormHooks
                    ? formCreditCardStandardOptions
                    : filteredCreditCardOptions
                }
                creditCardInfiniteScrollProps={
                  enableNewFormHooks
                    ? formCreditCards.standard.infiniteScrollProps
                    : creditCardInfiniteScrollProps
                }
                virtualCardPermissions={virtualCardPermissions}
                showSoftAlert={showPreApprovalSoftAlert}
                cardApproverId={cardApprovalRules?.approverId}
              />
            ))}
          {screen === UpdateVirtualCardScreen.IssuerCycle && (
            <UpdateVirtualCardIssuerCycleContent
              cardholder={cardholder}
              currencyError={currencyError}
              issuerCycleOptions={formCreditCards.issuerCycle.options}
              deleteAttachment={deleteAttachment}
              errorDetailMap={errorDetailMap}
              negativeButton={negativeButton}
              puttingVirtualCard={puttingVirtualCard}
              putVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPutVirtualCardAndOpenToastAndCloseFlyout
              }
              receiptAttachments={receiptAttachments}
              recipient={recipient}
              setScreen={setScreen}
              topLevelError={topLevelError}
              uploadAttachments={dispatchUploadAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              userOptions={[]}
              virtualCard={virtualCard}
              isFormDisabled={
                virtualCard.isPending &&
                canUpdateVirtualCard &&
                formCreditCards.issuerCycle.options.length === 0
              }
              labelCreditCardsAsAccounts={true}
              creditCardOptions={formCreditCardIssuerCycleOptions}
              creditCardInfiniteScrollProps={
                formCreditCards.issuerCycle.infiniteScrollProps
              }
              virtualCardPermissions={virtualCardPermissions}
              showSoftAlert={showPreApprovalSoftAlert}
              cardApproverId={cardApprovalRules?.approverId}
            />
          )}
          {screen === UpdateVirtualCardScreen.DecliningBalance && (
            <UpdateVirtualCardStandardContent
              cardholder={cardholder}
              currencyError={currencyError}
              deleteAttachment={deleteAttachment}
              errorDetailMap={errorDetailMap}
              negativeButton={negativeButton}
              puttingVirtualCard={puttingVirtualCard}
              putVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPutVirtualCardAndOpenToastAndCloseFlyout
              }
              receiptAttachments={receiptAttachments}
              recipient={recipient}
              setScreen={setScreen}
              topLevelError={topLevelError}
              uploadAttachments={dispatchUploadAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              userOptions={[]}
              virtualCard={virtualCard}
              isFormDisabled={
                virtualCard.isPending &&
                canUpdateVirtualCard &&
                (enableNewFormHooks
                  ? formStats.balanceLockCount === 0
                  : creditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCount ===
                    0)
              }
              labelCreditCardsAsAccounts={
                enableNewFormHooks
                  ? formStats.balanceLockSourceCount > 0
                  : creditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCount >
                    0
              }
              creditCardOptions={
                enableNewFormHooks
                  ? formCreditCardBalanceLockOptions
                  : filteredBalanceLockEnabledCreditCardOptions
              }
              creditCardInfiniteScrollProps={
                enableNewFormHooks
                  ? formCreditCards.balanceLock.infiniteScrollProps
                  : balanceLockedEnabledCreditCardInfiniteScrollProps
              }
              virtualCardPermissions={virtualCardPermissions}
              showSoftAlert={showPreApprovalSoftAlert}
              cardApproverId={cardApprovalRules?.approverId}
              typeofCard={VirtualCardTypes.decliningBalance}
            />
          )}
        </>
      ) : (
        <NotFoundRightFlyoutContent />
      )}
    </>
  )
}

export default UpdateVirtualCardForm
